import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDownloadLink } from "./../../api/storageApi";
import { postCustomTask } from "../../api/caseApi";
import { Download, ChevronDown, ChevronUp, AlertCircle } from "react-feather";
import Page from "../../Components/Page/Page";
import { Loader } from "../../Components/Loader/index";
import { FileList } from "../../Components/Common/FileList/FileList";
import { ReactComponent as TiffLogo } from "../../static/icons/tiff.svg";
import { ReactComponent as JsonLogo } from "../../static/icons/json.svg";
import { ReactComponent as NcLogo } from "../../static/icons/nc.svg";
import { ReactComponent as TextLogo } from "../../static/icons/text.svg";
import { ReactComponent as SimLogo } from "../../static/icons/sim.svg";
import { selectCurrentCase } from "../../redux/selectors/caseSelector";

import "./CaseFileBrowser.scss";

const simTextResults = [
  "cpu",
  "header",
  "parin",
  "runControl",
  "stdout",
  "cpuChld",
  "headerChld",
  "parinChld",
  "runControlChld",
  "stdoutChld",
];

const CaseFileBrowser = () => {
  const { t } = useTranslation();
  const caseDto = useSelector(selectCurrentCase);
  const currentCase = useSelector((state) => state.projects.currentCase);
  const osmNotification = useSelector((state) => state.tools.osmNotification);

  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [userUploadExpanded, setUserUploadExpanded] = useState(true);
  const [inputExpanded, setInputExpanded] = useState(false);
  const [monitoringExpanded, setMonitoringExpanded] = useState(false);
  const [outputExpanded, setOutputExpanded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const prevNotificationRef = useRef(null);

  const downloadFile = async (filePath) => {
    const url = await getDownloadLink(filePath);
    var link = document.createElement("a");
    link.download = "download";
    link.href = url;
    link.click();
  };

  const originalGeojson = {
    name: t("CityModel"),
    path: caseDto?.geojsonUrl || caseDto?.originalGeojsonUrl,
    icon: JsonLogo,
    readonly: true,
  };

  const geojson = {
    name: t("CheckedCityModel"),
    path: caseDto?.verifiedGeojsonUrl,
    icon: JsonLogo,
    readonly: true,
  };

  const originalTopography = {
    name: t("Topography"),
    path: caseDto?.originalTopographyUrl,
    icon: TiffLogo,
    readonly: true,
  };

  const userEdits = {
    name: t("UserEdits"),
    path: caseDto?.drawnGeojsonUrl,
    icon: JsonLogo,
    readonly: true,
  };

  const staticDriver = {
    name: t("StaticDriver"),
    path: caseDto?.staticDriverJob?.resultUrl || caseDto?.staticDriverUrl,
    readonly: true,
    icon: NcLogo,
  };

  const childStaticDriver = {
    name: t("ChildStaticDriver"),
    path: caseDto?.staticDriverJob?.childResultUrl,
    readonly: true,
    icon: NcLogo,
  };

  const simulationSetup = {
    name: t("SimulationSetup"),
    path: currentCase?.steering?.resultUrl || caseDto?.steering?.resultUrl,
    icon: SimLogo,
    readonly: true,
  };

  const childSimulationSetup = {
    name: t("ChildSimulationSetup"),
    path:
      currentCase?.steering?.childResultUrl ||
      caseDto?.steering?.childResultUrl,
    icon: SimLogo,
    readonly: true,
  };

  const windTurbineStaticParentFiles =
    currentCase?.staticDriverJob?.additionalData?.wind_turbine_statics_parent
      ?.map((item) => ({
        name: `StaticDriver_${item.direction}`,
        path: item.filepath,
        icon: NcLogo,
        readonly: true,
      }))
      .filter((f) => !!f.path) || [];

  const windTurbineStaticChildFiles =
    currentCase?.staticDriverJob?.additionalData?.wind_turbine_statics_child
      ?.map((item) => ({
        name: `StaticDriver_child_${item.direction}`,
        path: item.filepath,
        icon: NcLogo,
        readonly: true,
      }))
      .filter((f) => !!f.path) || [];

  const steeringWindTurbineFiles =
    currentCase?.steering?.additionalData?.pathsSteeringWindTurbine
      ?.map((item) => ({
        name: `SteeringFile_${item.direction}`,
        path: item.filepath,
        icon: TextLogo,
        readonly: true,
      }))
      .filter((f) => !!f.path) || [];

  const steeringWindTurbineChildFiles =
    currentCase?.steering?.additionalData?.pathsSteeringWindTurbineChild
      ?.map((item) => ({
        name: `SteeringFile_child_${item.direction}`,
        path: item.filepath,
        icon: TextLogo,
        readonly: true,
      }))
      .filter((f) => !!f.path) || [];

  const inputFiles = [
    geojson,
    userEdits,
    staticDriver,
    childStaticDriver,
    simulationSetup,
    childSimulationSetup,
    ...windTurbineStaticParentFiles,
    ...windTurbineStaticChildFiles,
    ...steeringWindTurbineFiles,
    ...steeringWindTurbineChildFiles,
  ].filter((f) => !!f.path);

  const monitoringFiles = useMemo(
    () =>
      caseDto?.simulationJobs?.reduce((files, job) => {
        return [
          ...files,
          ...job.results
            .filter((r) => {
              return simTextResults.some((prefix) =>
                r.fileType.startsWith(prefix)
              );
            })
            .map((r) => {
              return {
                name: t(r.fileType),
                path: r.filePath,
                icon: TextLogo,
                readonly: true,
              };
            }),
        ];
      }, []) ?? [],
    [caseDto?.simulationJobs, t]
  );

  const outputFiles = useMemo(
    () =>
      caseDto?.simulationJobs?.reduce((files, job) => {
        return [
          ...files,
          ...job.results
            .filter((r) => {
              return !simTextResults.some((prefix) =>
                r.fileType.startsWith(prefix)
              );
            })
            .map((r) => {
              return {
                name: t(r.fileType),
                path: r.filePath,
                icon: NcLogo,
                readonly: true,
                disabled: r.fileType.toLowerCase().startsWith("3d"),
              };
            }),
        ];
      }, []) ?? [],
    [caseDto?.simulationJobs, t]
  );

  const userUploadFiles = [originalGeojson, originalTopography].filter(
    (f) => !!f.path
  );

  const allFiles = [
    ...userUploadFiles,
    ...inputFiles,
    ...monitoringFiles,
    ...outputFiles,
  ];

  const selectableFiles = allFiles.filter((f) => !f.disabled);

  const handleFileCheckboxChange = (file, checked) => {
    if (file.disabled) return;

    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(file.path);
      } else {
        newSet.delete(file.path);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allFilePaths = selectableFiles.map((f) => f.path);
      setSelectedFiles(new Set(allFilePaths));
    } else {
      setSelectedFiles(new Set());
    }
  };

  const isFileChecked = (file) => {
    return selectedFiles.has(file.path);
  };

  const handleSectionSelectAll = (sectionFiles, checked) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        sectionFiles
          .filter((f) => !f.disabled)
          .forEach((f) => newSet.add(f.path));
      } else {
        sectionFiles.forEach((f) => newSet.delete(f.path));
      }
      return newSet;
    });
  };

  const isSectionAllChecked = (sectionFiles) => {
    const selectableSectionFiles = sectionFiles.filter((f) => !f.disabled);
    if (selectableSectionFiles.length === 0) return false;
    return selectableSectionFiles.every((f) => selectedFiles.has(f.path));
  };

  const expandAll = () => {
    setUserUploadExpanded(true);
    setInputExpanded(true);
    setMonitoringExpanded(true);
    setOutputExpanded(true);
  };

  const collapseAll = () => {
    setUserUploadExpanded(false);
    setInputExpanded(false);
    setMonitoringExpanded(false);
    setOutputExpanded(false);
  };

  const onSaveSelected = async () => {
    const selected = Array.from(selectedFiles)
      .map((filePath) => {
        const file = selectableFiles.find((f) => f.path === filePath);
        if (file) {
          return {
            name: file.name,
            url: filePath,
          };
        }
        return null;
      })
      .filter((x) => x !== null);

    if (selected.length === 0) {
      console.log("No files selected");
      return;
    }

    const caseId = currentCase?.id || caseDto?.id;

    if (!caseId) {
      console.error("Case ID not available");
      return;
    }

    try {
      setIsProcessing(true);
      setIsError(false);
      setCurrentRequestId(null);

      const taskRequest = {
        caseId: caseId,
        type: "multiCaseFilesDownloader",
        request: {
          files: selected,
        },
      };

      const requestId = await postCustomTask(taskRequest);
      console.log("Custom task requested for case files:", requestId);

      // Store requestId to track this specific task
      setCurrentRequestId(requestId);
      // setSelectedFiles(new Set());
      // setSelectAll(false);
    } catch (error) {
      console.error("Error requesting custom task:", error);
      setIsProcessing(false);
      setIsError(true);
    }
  };

  // Update selectAll state when selectedFiles changes
  useEffect(() => {
    const allSelected =
      selectableFiles.length > 0 &&
      selectableFiles.every((f) => selectedFiles.has(f.path));
    setSelectAll(allSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles]);

  // Track notification status for multiCaseFilesDownloader tasks
  useEffect(() => {
    if (osmNotification && currentRequestId) {
      const notification = osmNotification;
      const prevNotification = prevNotificationRef.current;

      // Only process notifications for multiCaseFilesDownloader tasks
      if (
        notification.type === "multiCaseFilesDownloader" &&
        notification.requestId === currentRequestId
      ) {
        // Status 2 = finished successfully
        if (notification.status === 2 && prevNotification?.status !== 2) {
          setIsProcessing(false);
          setIsError(false);
        }
        // Status 3 = error
        else if (prevNotification?.status !== 3 && notification.status === 3) {
          setIsError(true);
          setIsProcessing(false);
        }
        // Status 0 or 1 = processing (initializing or running)
        else if (
          (notification.status === 0 || notification.status === 1) &&
          !isProcessing
        ) {
          setIsProcessing(true);
          setIsError(false);
        }
      }
    }
    prevNotificationRef.current = osmNotification;
  }, [osmNotification, currentRequestId, isProcessing]);

  return (
    <Page>
      {caseDto ? (
        <>
          <h2 style={{ fontSize: 28 }}>{t("Files")}</h2>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "450px",
              marginBottom: "14px",
            }}
          >
            <div style={{ display: "flex", marginRight: "12px" }}>
              <input
                type="checkbox"
                style={{ width: "12px" }}
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
              <span
                style={{
                  fontSize: "12px",
                  marginLeft: "4px",
                  alignContent: "center",
                }}
              >
                {t("SelectAll")}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
              }}
            >
              <button
                className="btn-modal-cancel"
                onClick={expandAll}
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "#444",
                  padding: "1px 5px",
                  minWidth: "auto",
                  justifyContent: "center",
                  border: "1px solid #a7a7a7",
                  marginRight: "0px",
                }}
                title={t("ExpandAll")}
              >
                <ChevronDown size={"12px"} strokeWidth={"1.8px"} />
              </button>
              <button
                className="btn-modal-cancel"
                onClick={collapseAll}
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "#444",
                  padding: "1px 5px",
                  minWidth: "auto",
                  justifyContent: "center",
                  border: "1px solid #a7a7a7",
                }}
                title={t("CollapseAll")}
              >
                <ChevronUp size={"12px"} strokeWidth={"1.8px"} />
              </button>
            </div>
          </div>
          <FileList
            items={userUploadFiles}
            label={t("UserUpload")}
            expanded={userUploadExpanded}
            onDownload={(f) => downloadFile(f.path)}
            showCheckbox={true}
            isFileChecked={isFileChecked}
            onCheckboxChange={(e) => {
              const filePath = e.target.getAttribute("data-file-path");
              const file = allFiles.find((f) => f.path === filePath);
              if (file) {
                handleFileCheckboxChange(file, e.target.checked);
              }
            }}
            showHeaderCheckbox={true}
            headerCheckboxChecked={isSectionAllChecked(userUploadFiles)}
            onHeaderCheckboxChange={(checked) =>
              handleSectionSelectAll(userUploadFiles, checked)
            }
          />
          <FileList
            items={inputFiles}
            label={t("Input")}
            expanded={inputExpanded}
            onDownload={(f) => downloadFile(f.path)}
            showCheckbox={true}
            isFileChecked={isFileChecked}
            onCheckboxChange={(e) => {
              const filePath = e.target.getAttribute("data-file-path");
              const file = allFiles.find((f) => f.path === filePath);
              if (file) {
                handleFileCheckboxChange(file, e.target.checked);
              }
            }}
            showHeaderCheckbox={true}
            headerCheckboxChecked={isSectionAllChecked(inputFiles)}
            onHeaderCheckboxChange={(checked) =>
              handleSectionSelectAll(inputFiles, checked)
            }
          />
          <FileList
            items={monitoringFiles}
            label={t("Monitioring")}
            expanded={monitoringExpanded}
            onDownload={(f) => downloadFile(f.path)}
            showCheckbox={true}
            isFileChecked={isFileChecked}
            onCheckboxChange={(e) => {
              const filePath = e.target.getAttribute("data-file-path");
              const file = allFiles.find((f) => f.path === filePath);
              if (file) {
                handleFileCheckboxChange(file, e.target.checked);
              }
            }}
            showHeaderCheckbox={true}
            headerCheckboxChecked={isSectionAllChecked(monitoringFiles)}
            onHeaderCheckboxChange={(checked) =>
              handleSectionSelectAll(monitoringFiles, checked)
            }
          />
          <FileList
            items={outputFiles}
            label={t("Output")}
            expanded={outputExpanded}
            onDownload={(f) => downloadFile(f.path)}
            showCheckbox={true}
            isFileChecked={isFileChecked}
            onCheckboxChange={(e) => {
              const filePath = e.target.getAttribute("data-file-path");
              const file = allFiles.find((f) => f.path === filePath);
              if (file) {
                handleFileCheckboxChange(file, e.target.checked);
              }
            }}
            showHeaderCheckbox={true}
            headerCheckboxChecked={isSectionAllChecked(outputFiles)}
            onHeaderCheckboxChange={(checked) =>
              handleSectionSelectAll(outputFiles, checked)
            }
          />
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              flexDirection: "column",
              width: "450px",
            }}
          >
            {isProcessing && (
              <div>
                <div
                  style={{
                    display: "flex",
                    marginTop: -25,
                    justifyContent: "center",
                  }}
                >
                  <Loader size="small-osm" style={{ marginLeft: "0px" }} />
                </div>
                <p
                  style={{
                    fontSize: 11,
                    cursor: "default",
                    color: "gray",
                    textAlign: "center",
                  }}
                >
                  {t("Processing")}...
                </p>
              </div>
            )}

            {!isProcessing && isError && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <AlertCircle size="12px" color="#ac4141" />
                <p
                  className="new-layer-name-error"
                  style={{
                    fontSize: "12px",
                  }}
                >
                  {t("ErrorProcessingFiles") ||
                    "An error occurred while processing files. Please try again."}
                </p>
              </div>
            )}

            <button
              className="btn-modal-cancel"
              onClick={onSaveSelected}
              disabled={selectedFiles.size === 0 || isProcessing}
              style={{
                display: "flex",
                alignItems: "center",
                color: "#444",
                padding: "5px 6px",
                minWidth: "110px",
                width: "auto",
                maxWidth: "none",
                alignSelf: "flex-end",
                justifyContent: "space-around",
                marginTop: isProcessing ? "-40px" : "0px",
                opacity: selectedFiles.size === 0 || isProcessing ? 0.5 : 1,
                cursor:
                  selectedFiles.size === 0 || isProcessing
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              <div style={{ display: "flex" }}>
                <Download size={"14px"} strokeWidth={"1.8px"} />
              </div>
              {t("SaveSelected")}
            </button>
          </div>
        </>
      ) : (
        <Loader />
      )}
    </Page>
  );
};

export default withRouter(CaseFileBrowser);
